import { HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from '@/core/services/api/api.service';
import {
  Banque,
  CreditDemande,
  Employeur,
  EmployeurDocument,
  EmployeurObservation,
  EmployeurSecteurActivite,
} from '../../interfaces/credit.interface';

@Injectable({ providedIn: 'root' })
export class CreditEmployeurService {
  private readonly api = inject(ApiService);
  private readonly endpoint = '/credit';

  getListeEmployeurs(action?: 'valide' | 'rejette') {
    const url = action
      ? `${this.endpoint}/listeEmployeur?action=${action}`
      : `${this.endpoint}/listeEmployeur`;
    return this.api.get<{ employeurs: Employeur[] }>(url).pipe(
      map((res) => res.employeurs ?? []),
      catchError((err) => throwError(() => err)),
    );
  }

  getDetailEmployeur(id: number) {
    return this.api.get<{ employeur: Employeur }>(this.endpoint + '/detailsEmployeur/' + id).pipe(
      map((res) => res.employeur),
      catchError((err) => throwError(() => err)),
    );
  }

  getObservationsEmployeur(id: number) {
    // Le backend renvoie historiquement la liste sous la clé `data` (cf. legacy
    // detail-employeur-credit.component.ts:203). On tolère les deux clés.
    return this.api
      .get<{
        observations?: EmployeurObservation[];
        data?: EmployeurObservation[];
      }>(this.endpoint + '/observationEmployeur/' + id)
      .pipe(
        map((res) => res.observations ?? res.data ?? []),
        catchError((err) => throwError(() => err)),
      );
  }

  getDocumentsEmployeur(id: number) {
    return this.api
      .get<{ documents: EmployeurDocument[] }>(this.endpoint + '/employeurDocument/' + id)
      .pipe(
        map((res) => res.documents ?? []),
        catchError((err) => throwError(() => err)),
      );
  }

  getSecteurActivites() {
    return this.api
      .get<{ TypeActivites: EmployeurSecteurActivite[] }>(this.endpoint + '/gettypeactivite')
      .pipe(map((res) => res.TypeActivites ?? []));
  }

  getBanques() {
    return this.api.get<{ banque: Banque[] }>('/banque/liste').pipe(map((res) => res.banque ?? []));
  }

  saveEmployeur(data: unknown) {
    return this.api.post<{ status: number; message?: string }>(
      this.endpoint + '/saveEmployeur',
      data,
    );
  }

  updateEmployeur(data: unknown) {
    return this.api.put<{ status: number; message?: string }>(
      this.endpoint + '/employeur/update',
      data,
    );
  }

  updateNumeroPerfect(data: { employeur: number; numPerfect: string }) {
    return this.api.put<{ status: number; message?: string }>(
      this.endpoint + '/employeur/updatePerfectNumber',
      data,
    );
  }

  actionEmployeur(data: unknown) {
    return this.api.post<{ status: number; message?: string }>(
      this.endpoint + '/valadationEmployeur',
      data,
    );
  }

  uploadDocument(employeurId: number, libelle: string, description: string, file: File) {
    const fd = new FormData();
    fd.append('employeur', String(employeurId));
    fd.append('libelle', libelle);
    fd.append('description', description);
    fd.append('document', file);
    return this.api.postFormData<{ status: number; message?: string }>(
      this.endpoint + '/saveDocEmployeur',
      fd,
    );
  }

  deleteDocument(id: number | string) {
    return this.api.delete<{ status: number }>(this.endpoint + '/deleteDocEmployeur/' + id);
  }

  getDemandesEmployeur(id: number) {
    const params = new HttpParams().set('employeur', id);
    return this.api.get<{ demande: CreditDemande[] }>(this.endpoint + '/listeDemande', params).pipe(
      map((res) => res.demande ?? []),
      catchError(() => of([])),
    );
  }
}
